// Shared bulk-buy discount logic usable by the Nuxt app, Nitro routes and
// Cloud Functions. No Vue imports; pure TypeScript utilities.
//
// Two discount modes are supported per SubKrewe:
// - 'percent': take `discount_percent` off the current tier's unit price.
// - 'fixed':   charge `discount_fixed_price` per membership regardless of what
//              the current tier costs. Used to honour a promised price after
//              tiered pricing has already moved up.
//
// Both modes share the optional cap, so a cap always means "the first N
// memberships this season get the discounted price".
//
// Cap accounting is scoped per parade year in `discount_consumed_by_year`,
// because everything else about bulk buying already is — pool tickets live
// under `ticket_pools/{year}` and purchases under `purchases[year]`. The flat
// `discount_consumed` field it replaces was a lifetime counter, so last
// season's purchases ate into a cap set for this one. That legacy field is
// deliberately never read: it also counted *undiscounted* Eventbrite bulk
// purchases, so its values do not mean what its name says.

export type BulkDiscountType = 'percent' | 'fixed'

export const BULK_DISCOUNT_TYPES: readonly BulkDiscountType[] = ['percent', 'fixed']

/**
 * Raw SubKrewe document fields a config is derived from. Values are `unknown`
 * so a Firestore `DocumentData` / `Record<string, unknown>` can be handed
 * straight in alongside a typed `SubKrewe`; every field is validated below.
 */
export interface BulkDiscountSource {
  discount_type?: unknown
  discount_percent?: unknown
  discount_fixed_price?: unknown
  discount_cap?: unknown
  discount_consumed_by_year?: unknown
}

/**
 * The discount as configured — everything that does not depend on how much of
 * it has been used. Enough to price a membership or label a button.
 */
export interface BulkDiscountTerms {
  type: BulkDiscountType
  /** 0-100 float. Always 0 when type is 'fixed'. */
  percentOff: number
  /** Per-membership price in cents. Always null when type is 'percent'. */
  fixedPrice: number | null
  /** Max discounted memberships per parade year; null means uncapped. */
  cap: number | null
}

/**
 * Terms plus how much of the cap a specific parade year has used. Required for
 * anything that decides how many memberships still get the discount — which is
 * why the parade year is a mandatory argument rather than an optional one.
 */
export interface BulkDiscountConfig extends BulkDiscountTerms {
  /** Parade year this consumption count belongs to. */
  year: string
  /** Discounted memberships already consumed in `year`. */
  consumed: number
}

/** Firestore field path for a year's consumption counter. */
export function consumedFieldPath(year: string | number): string {
  return `discount_consumed_by_year.${year}`
}

export interface BulkPricingBreakdown {
  discountedQuantity: number
  fullPriceQuantity: number
  /** Cents per membership for the discounted portion. */
  discountedUnitAmount: number
  /** Cents per membership for the undiscounted remainder. */
  fullPriceUnitAmount: number
  /** Membership subtotal in cents, before the processing fee. */
  subtotalCents: number
  /** Cents saved against buying the whole quantity at tier price. */
  discountAmountCents: number
}

export const NO_BULK_DISCOUNT_TERMS: BulkDiscountTerms = {
  type: 'percent',
  percentOff: 0,
  fixedPrice: null,
  cap: null,
}

export function noBulkDiscount(year: string | number): BulkDiscountConfig {
  return { ...NO_BULK_DISCOUNT_TERMS, year: String(year), consumed: 0 }
}

function finiteNumber(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null
}

/**
 * Normalise the configured discount on a SubKrewe document. `discount_type` is
 * absent on SubKrewes configured before fixed pricing existed, so it defaults
 * to 'percent'. Fields belonging to the inactive mode are zeroed out rather
 * than carried along, so callers never have to ask which of the two prices is
 * the live one.
 */
export function readBulkDiscountTerms(source: BulkDiscountSource | null | undefined): BulkDiscountTerms {
  const type: BulkDiscountType = source?.discount_type === 'fixed' ? 'fixed' : 'percent'

  const rawPercent = finiteNumber(source?.discount_percent) ?? 0
  const percentOff = type === 'percent' ? Math.min(100, Math.max(0, rawPercent)) : 0

  const rawFixed = finiteNumber(source?.discount_fixed_price)
  const fixedPrice = type === 'fixed' && rawFixed !== null && rawFixed > 0
    ? Math.round(rawFixed)
    : null

  const rawCap = finiteNumber(source?.discount_cap)
  const cap = rawCap !== null && rawCap > 0 ? Math.floor(rawCap) : null

  return { type, percentOff, fixedPrice, cap }
}

/** Discounted memberships consumed in one parade year. */
export function readConsumedForYear(
  source: BulkDiscountSource | null | undefined,
  year: string | number,
): number {
  const byYear = source?.discount_consumed_by_year
  if (!byYear || typeof byYear !== 'object') return 0
  const raw = finiteNumber((byYear as Record<string, unknown>)[String(year)])
  return raw !== null && raw > 0 ? Math.floor(raw) : 0
}

/**
 * The discount as it applies to one parade year. The year is required: cap
 * arithmetic against the wrong year is the bug this signature exists to
 * prevent.
 */
export function readBulkDiscountConfig(
  source: BulkDiscountSource | null | undefined,
  year: string | number,
): BulkDiscountConfig {
  return {
    ...readBulkDiscountTerms(source),
    year: String(year),
    consumed: readConsumedForYear(source, year),
  }
}

/**
 * True when the config would actually change what a captain pays. A fixed
 * price of 0 counts as no discount: free tickets are minted as comps through
 * the admin pool tools instead of run through a $0 checkout.
 */
export function isBulkDiscountActive(config: BulkDiscountTerms): boolean {
  if (config.type === 'fixed') {
    return config.fixedPrice !== null && config.fixedPrice > 0
  }
  return config.percentOff > 0
}

/** Discounted memberships still available under the cap; null when uncapped. */
export function remainingDiscounted(config: BulkDiscountConfig): number | null {
  if (!isBulkDiscountActive(config)) return 0
  if (config.cap === null) return null
  return Math.max(0, config.cap - config.consumed)
}

/** How many of `quantity` memberships get the discounted price. */
export function resolveDiscountedQuantity(config: BulkDiscountConfig, quantity: number): number {
  if (!isBulkDiscountActive(config) || quantity <= 0) return 0
  const remaining = remainingDiscounted(config)
  return remaining === null ? quantity : Math.min(quantity, remaining)
}

/**
 * Per-membership price in cents for one discounted unit. A fixed price above
 * the current tier is clamped down to the tier price, so a stale fixed price
 * can never charge more than buying normally would.
 */
export function discountedUnitAmount(config: BulkDiscountTerms, tierUnitAmount: number): number {
  if (!isBulkDiscountActive(config)) return tierUnitAmount
  if (config.type === 'fixed') {
    const fixed = config.fixedPrice
    if (fixed === null) return tierUnitAmount
    return Math.max(0, Math.min(fixed, tierUnitAmount))
  }
  return Math.max(0, tierUnitAmount - Math.round((tierUnitAmount * config.percentOff) / 100))
}

/**
 * Split `quantity` memberships into a discounted and a full-price portion and
 * price each. The two portions are billed as separate Stripe line items, which
 * is what keeps a capped discount from leaking onto the units past the cap.
 */
export function computeBulkPricing(
  config: BulkDiscountConfig,
  tierUnitAmount: number,
  quantity: number,
): BulkPricingBreakdown {
  const discountedQuantity = resolveDiscountedQuantity(config, quantity)
  const fullPriceQuantity = Math.max(0, quantity - discountedQuantity)
  const unit = discountedQuantity > 0
    ? discountedUnitAmount(config, tierUnitAmount)
    : tierUnitAmount
  const subtotalCents = unit * discountedQuantity + tierUnitAmount * fullPriceQuantity

  return {
    discountedQuantity,
    fullPriceQuantity,
    discountedUnitAmount: unit,
    fullPriceUnitAmount: tierUnitAmount,
    subtotalCents,
    discountAmountCents: tierUnitAmount * quantity - subtotalCents,
  }
}

export function formatCents(cents: number, currency = 'usd'): string {
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency.toUpperCase(),
    }).format(cents / 100)
  }
  catch {
    return `$${(cents / 100).toFixed(2)}`
  }
}

/**
 * Short label for the discount itself, e.g. "15% off" or "$40.00 each".
 * Returns null when no discount is configured.
 */
export function bulkDiscountLabel(config: BulkDiscountTerms): string | null {
  if (!isBulkDiscountActive(config)) return null
  if (config.type === 'fixed') {
    const fixed = config.fixedPrice
    if (fixed === null) return null
    return `${formatCents(fixed)} each`
  }
  return `${config.percentOff}% off`
}
